<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="content-container">
    <!-- AI result data contract changes require a new AI_CACHE_CUR_VERSION -->
    <div v-for="(result, topicIndex) in searchResults" 
         :key="result.topicTitle + topicIndex" 
         class="ai-result">
      <div class="result-body">
        <!-- Class changes may break GVD & hovertooltip logic -->
        <div id="ai-result-snippet"
             class="snippet-container utd-longform-content-styles-v1">
          <span class="snippet"
                :section="getSection(result?.sectionHierarchy)"
                :section-id="result?.sectionId"
                :display-rank="result?.passageNum"
                :search-rank="result?.searchRank"
                v-html="result.snippet" /><!--
       --><a id="read-more"
             class="topic-read-more"
             :href="getTopicSectionHref(result, topicIndex + 1, { isReadMore: true })">
            Read More
          </a>
        </div>
        <utd-media-collection class="passage-graphic collection-type-thumbnails"
                              :media-collection="result.associatedGraphics"
                              :skip-sidebar-state-reset="false" />
      </div>
      <div class="breadcrumbs">
        <span class="source">SOURCE:</span><!--
       --><a class="breadcrumbs-link"
             :href="getTopicSectionHref(result, topicIndex + 1)">
          <span class="topic-title breadcrumb"> 
            <span> {{ result.topicTitle }} </span>
          </span><!--
       --><span v-for="(section, sectionIndex) in result.sectionHierarchy"
                :key="`${section}: ${topicIndex}-${sectionIndex}`" 
                class="breadcrumb">
            <span> {{ section }} </span>
          </span>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import UtdMediaCollection from '_acaSrc/components/shared/stdlib/UtdMediaCollection.vue';
import { C_AI_SEARCH } from '_acaSrc/utility/constants';
import { setSearchUrlParamsHelper } from '_acaSrc/utility/http';
import { mapGetters } from 'vuex';

export default {
    components: {
        UtdMediaCollection
    },
    props: {
        searchResults: {
            type: Array
        }
    },
    data() {
        return {
            hasSetPassageNum: false
        };
    },
    computed: {
        ...mapGetters('search', [ 'searchFor' ])
    },
    methods: {
        getSection(sectionHierarchy) {
            if (!Array.isArray(sectionHierarchy) || sectionHierarchy.length < 1) {
                return;
            }
            return sectionHierarchy[sectionHierarchy.length - 1] || '';
        },
        getHighestRank(topic, rankType) {
            if (!topic?.passages || !Array.isArray(topic.passages)) {
                return;
            }
            const highestRankPassage = topic.passages.reduce(
                (accumulator, currentValue) => {
                    const accumulatorVal = accumulator[rankType] || null;
                    const curVal = currentValue[rankType] || null;
                    return accumulatorVal && accumulatorVal <= curVal
                        ? accumulator
                        : currentValue;
                }
            );
            return highestRankPassage[rankType] || null;
        },
        // eslint-disable-next-line complexity
        generateTopicEventQps(result, curHref, resultNum, options) {
            const rankIndex = resultNum || '';
            const searchInfo = result?.searchRank || '';
            const passageDigest = result?.passageDigestHex || '';

            const params = {
                sourceValue: options?.isReadMore ?
                    C_AI_SEARCH.EVENTS.TOPIC_SOURCE_RM :
                    C_AI_SEARCH.EVENTS.TOPIC_SOURCE_BREADCRUMBS,
                topicRef: result?.topicId || '',
                usageType: C_AI_SEARCH.EVENTS.USAGE_TYPE,
                anchor: result?.sectionId || '',
                searchTerm: this.searchFor || '',
                passageDigest,
                rankIndex,
                searchInfo
            };

            return setSearchUrlParamsHelper(curHref, params);
        },
        getTopicSectionHref(result, resultNum, options) {
            let href = `/contents/${result.topicId}?source=`;
            href = this.generateTopicEventQps(result, href, resultNum, options);
            href += `#${result.sectionId}`;
            return href;
        }
    }
};
</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';
@import (reference) "~_acaAssets/global/variables.less";

.content-container {
  .ds1-pb-2();
  .ds1-mb-2();
  max-width: 1868px;
  border-bottom: 1px solid @WKCE-GRAY-TINT4;

  .ai-result {
    .ds1-pt-2();

    &:nth-of-type(n + 2) { // Line between every topic following the first topic
      .ds1-mt-2();
      border-top: 1px solid @WKCE-GRAY-TINT4;
    }

    
    .result-body {
      // Flex styles used for featured graphics (not Phase 1)
      display: flex; 
      justify-content: space-between;
      width: 100%;

      .snippet-container {
        font-size: 16px;
        line-height: 26px;

        &.utd-longform-content-styles-v1 .snippet :deep(a) {
          color: @WK_UTD_BLUE_HOVER;
        }

        .snippet {
          display: block;

          > :deep(div) {
            display: inline;

            > p:first-child {
              .ds1-mt-0();
            }

            > p:last-child {
              .ds1-mb-0();
            }
          }
        }

        .topic-read-more {
          display: inline-block;
          margin-left: -1px; // UX request
          font-weight: 600;
          font-size: 11px;
          color: @WKCE-BLUE-SHADE1;
          transition: border-color 0.25s ease-in-out,
                      background-color 0.25s ease-in-out;
          white-space: nowrap;
          padding: 2px 22px 2px 6px;
          background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTMiCgkgaGVpZ2h0PSIxMiIKCSB2aWV3Qm94PSIwIDAgMTMgMTIiCgkgZmlsbD0ibm9uZSIKCSB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgoJPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0gNi45NzIgMC40IEwgMi4xNzIgMC40IEwgMi4xNzIgMTEuNiBMIDEwLjk3MiAxMS42IEwgMTAuOTcyIDQuNCBMIDYuOTcyIDAuNCBaIiBzdHlsZT0iZmlsbC1ydWxlOiBub256ZXJvOyBwYWludC1vcmRlcjogZmlsbDsgZmlsbDogcmdiKDI1NSwgMjU1LCAyNTUpOyIgaWQ9Im9iamVjdC0wIi8+Cgk8cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTSA3IDAuNCBMIDIuMiAwLjQgTCAyLjIgMTEuNiBMIDExIDExLjYgTCAxMSA0LjQgTCA3IDAuNCBaIE0gOS44NSA0LjQgTCA3IDQuNCBMIDcgMS41NSBMIDkuODUgNC40IFogTSAzIDEwLjggTCAxMC4yIDEwLjggTCAxMC4yIDUuMiBMIDYuMiA1LjIgTCA2LjIgMS4yIEwgMyAxLjIgTCAzIDEwLjggWiIgZmlsbD0iIzAwNUI5MiIgc3R5bGU9IiIvPgoJPHJlY3QgeD0iNC4wNzIxNCIgeT0iOC41IiB3aWR0aD0iNSIgaGVpZ2h0PSIwLjUiIGZpbGw9IiMwMDVCOTIiLz4KCTxyZWN0IHg9IjQuMDcyMTQiIHk9IjciIHdpZHRoPSI0IiBoZWlnaHQ9IjAuNSIgZmlsbD0iIzAwNUI5MiIvPgo8L3N2Zz4=);
          background-repeat: no-repeat;
          background-size: 14px;
          background-position-y: 4px;
          background-position-x: 67px;
          background-color: @WKCE-WHITE;
          border: 1px solid @WKCE-BLUE-TINT3;
          border-radius: 7px;
          line-height: 16px;

          &:hover {
            border: 1px solid @WKCE-BLUE-TINT3;
            background-color: @WKCE-BLUE-TINT3;
            color: @WKCE-BLUE-SHADE2;
            background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTMiCgkgaGVpZ2h0PSIxMiIKCSB2aWV3Qm94PSIwIDAgMTMgMTIiCgkgZmlsbD0ibm9uZSIKCSB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgoJPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0gNi45NzIgMC40IEwgMi4xNzIgMC40IEwgMi4xNzIgMTEuNiBMIDEwLjk3MiAxMS42IEwgMTAuOTcyIDQuNCBMIDYuOTcyIDAuNCBaIiBzdHlsZT0iZmlsbC1ydWxlOiBub256ZXJvOyBwYWludC1vcmRlcjogZmlsbDsgZmlsbDogcmdiKDI1NSwgMjU1LCAyNTUpOyIgaWQ9Im9iamVjdC0wIi8+Cgk8cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTSA3IDAuNCBMIDIuMiAwLjQgTCAyLjIgMTEuNiBMIDExIDExLjYgTCAxMSA0LjQgTCA3IDAuNCBaIE0gOS44NSA0LjQgTCA3IDQuNCBMIDcgMS41NSBMIDkuODUgNC40IFogTSAzIDEwLjggTCAxMC4yIDEwLjggTCAxMC4yIDUuMiBMIDYuMiA1LjIgTCA2LjIgMS4yIEwgMyAxLjIgTCAzIDEwLjggWiIgZmlsbD0iIzAwM0Q2MSIgc3R5bGU9IiIvPgoJPHJlY3QgeD0iNC4wNzIxNCIgeT0iOC41IiB3aWR0aD0iNSIgaGVpZ2h0PSIwLjUiIGZpbGw9IiMwMDNENjEiLz4KCTxyZWN0IHg9IjQuMDcyMTQiIHk9IjciIHdpZHRoPSI0IiBoZWlnaHQ9IjAuNSIgZmlsbD0iIzAwM0Q2MSIvPgo8L3N2Zz4=);
          }
        }
      }

      .passage-graphic { // Featured graphic support (not Phase 1)
        .ds1-mt-1();

        :deep(.collection-thumbnails) {
          flex-direction: column;

          .utd-thumbnail__container {
            display: flex;
            align-items: center;
            width: 245px;
            height: 92px;
            margin: 0;

            &:nth-child(n + 2) {
              margin-top: 10px;
            }

            .utd-thumbnail__image-container {
              width: 65px;
              height: 65px;

              .utd-thumbnail__image-anchor {
                width: 65px;
                height: 65px;
              }
            }

            .utd-thumbnail__label-anchor {
              .ds1-mv-0();
              .ds1-mh-1();
              width: 100%; // IE11 support
              height: auto;
              font-size: 9.5px;
              line-height: 14px;
              color: @WKCE-BLUE-SHADE1;
            }
          }
        }
      }
    }

    .breadcrumbs {
      .ds1-mt-2();
      min-height: 24px;

      .source {
        .ds1-mr-1();
        display: block;
        font-size: 12px;
        font-weight: 600;
        color: @WKCE-GREEN-SHADE2;
        letter-spacing: 1px;
      }

      .breadcrumbs-link {
        text-decoration: none;
        border-bottom: 1px solid transparent;
        transition: border-color 0.25s ease-in-out;

        &:hover {
          border-bottom: 1px solid @WKCE-BLUE-SHADE1;
        }
      }

      .breadcrumb {
        .ds1-mr-1();
        white-space: nowrap;
        font-size: 12px;
        font-weight: 400;

        > span {
          white-space: balance;
          text-wrap: balance;
        }

        &.topic-title {
          font-size: 14px;
          white-space: balance;
          text-wrap: balance;
        }
        
        &::before { // Chevron before each breadcrumb
          .ds1-mr-1();
          content: @WK_ICON_CHEVRON_RIGHT;
          font-family: wk-icons-open; /* stylelint-disable-line font-family-no-missing-generic-family-keyword */
          width: 12px;
          height: 12px;
          font-size: 10px;
          color: @WKCE-BLUE-SHADE1;
          vertical-align: 1px;
          font-weight: 400;
          text-decoration: none;
          display: inline-block;
        }

        &:first-child::before { // No chevron before first breadcrumb
          display: none;
        }

        &:last-child {
          .ds1-mr-0();
        }
      }
    }
  }
}

@media only screen and (min-width: 768px) { 
  .isDesktop .content-container {
    .ds1-mb-0();
    border-bottom: none;
  }

  .isDesktop .ai-result {
    .ds1-pt-3();
    margin-right: 36px;
    margin-left: 36px;

    &:nth-of-type(n + 2) {
      .ds1-mt-3();
    }

    .breadcrumbs .source {
      display: inline;
    }

    #ai-result-snippet {
      line-height: 24px;
    }
  }
}
</style>